/* eslint-disable react/no-danger */
import React, { useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Cookies from "js-cookie";
import _has from "lodash/has";

import ReactTable from "components/lib/Global/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import {
  calculateCumulativeOwnership,
  delay,
  getBeneficialOwnershipFormFields,
  getFullName,
  getMaskedEmail,
  roundNumber,
} from "utils";
import { PlusIcon } from "components/lib/Shared/Icons/sflIcons";
import BeneficialOwnersSubRowAsync from "./BeneficialOwnersSubRowReview";
import NotificationBanner from "components/lib/Shared/NotificationBanner";
import TypeOfControlColumn from "components/lib/Shared/TypeOfControlColumn";
import { deleteBeneficialOwnerAsync } from "state/slices/tables/beneficialOwnersTableSlice";
import { CORPORATES } from "components/PLCTABS/SFLdata/config/constants";
import { Constants } from "config/constants";
import AvatarIcon from "components/lib/Shared/Icons/AvatarIcon";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import ChangeOfParticularsSubRow from "components/CompanyDashboard/MaintainYourCompany/Changes/OfficialsShareholdersAuthorisedPersons/ChangeOfParticulars/ChangeOfParticularsSubRow";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import Tooltip from "components/lib/Shared/Tooltip";

const BeneficialOwnersTableReview = ({
  data,
  showActionColumn,
  handleBackBtn,
  groupBOEntries = true,
  hideColumnsRow,
  selectedTableType,
  hideEditBtn,
  hideDeleteBtn,
  hideReviewWorkflow,
  isInManualUpdate,
  renderShortTypeOfControl,
  disableDeleteEffect,
  hideResolveAllCheckbox,
  showDocumentLabel,
  transactions = [],
  ...restProps
}) => {
  const role = Cookies.get("role");
  const dispatch = useDispatch();
  const [isViewOnly, setIsViewOnly] = useState(false);
  const collapseBtnRef = useRef(null);
  const { company } = useSelector((state) => state.companySlice);
  const currentUser = useSelector((state) => state.userSlice);
  const params = useParams();
  const companyId = company?._id ?? params?.companyId;
  const beneficialOwnersTableSlice = useSelector(
    (state) => state.beneficialOwnersTableSlice
  );
  const companyOfficialsSlice =
    useSelector((state) => state.companyOfficialsSlice) || {};
  const originalCompanyOfficials =
    companyOfficialsSlice.companyOfficials?.items || [];
  const { comments } = useSelector((state) => state.commentsSlice);
  const { isInUpdateCompanyDetailsContext, selectionsMade } = useContext(
    UpdateCompanyDetailsContext
  );
  const { setChangeOfParticularsInfo } = useContext(ManageModuleContext);
  let changesBeneficialOwners =
    selectionsMade?.beneficialOwners?.changesBeneficialOwners;

  const { deleteBeneficialOwnerStatus } = beneficialOwnersTableSlice;
  const beneficialOwners =
    restProps.beneficialOwners || beneficialOwnersTableSlice.beneficialOwners;
  const beneficialOwnersDropdown = beneficialOwners.filter(
    (bo) => bo?.showInBoDropdown
  );
  const allBeneficialOwners = [
    ...beneficialOwners,
    ...beneficialOwnersDropdown,
  ];
  const [changeOfParticularsIsSelected, setChangeOfParticularsIsSelected] =
    useState(false);

  // Extract beneficial ownership changes from transactions
  const beneficialOwnershipChanges = React.useMemo(() => {
    const changesMap = new Map();
    if (!transactions || !Array.isArray(transactions)) {
      if (isInManualUpdate) {
      }
      return changesMap;
    }

    if (isInManualUpdate) {
    }

    transactions.forEach((transaction, txIndex) => {
      if (isInManualUpdate) {
      }

      if (transaction?.transactionType !== "MANUAL") {
        if (isInManualUpdate) {
        }
        return;
      }

      // Process shareCapitalChanges allocations
      if (Array.isArray(transaction.shareCapitalChanges)) {
        if (isInManualUpdate) {
        }

        transaction.shareCapitalChanges.forEach((change, changeIndex) => {
          if (isInManualUpdate) {
          }

          if (Array.isArray(change.allocations)) {
            change.allocations.forEach((allocation, allocIndex) => {
              let officialId = allocation?.officialId;
              const beneficialOwnershipDetails =
                allocation?.beneficialOwnershipDetails;

              if (isInManualUpdate) {
              }

              // Handle both string and ObjectId formats
              if (typeof officialId === "object" && officialId?.$oid) {
                officialId = officialId.$oid;
                if (isInManualUpdate) {
                }
              }

              if (officialId && beneficialOwnershipDetails) {
                if (isInManualUpdate) {
                }
                changesMap.set(officialId, {
                  ...beneficialOwnershipDetails,
                  allocation: allocation,
                });
              } else {
                if (isInManualUpdate) {
                }
              }
            });
          } else {
            if (isInManualUpdate) {
            }
          }
        });
      } else {
        if (isInManualUpdate) {
        }
      }
    });

    if (isInManualUpdate) {
      if (changesMap.size > 0) {
      }
    }

    return changesMap;
  }, [transactions, isInManualUpdate]);

  // Sort data to show officials with beneficial ownership changes first
  const sortedData = React.useMemo(() => {
    if (!data || data.length === 0) return data;

    const sorted = [...data].sort((a, b) => {
      const aHasChanges = beneficialOwnershipChanges.has(a._id) || a.hasChanges;
      const bHasChanges = beneficialOwnershipChanges.has(b._id) || b.hasChanges;

      // Officials with changes come first
      if (aHasChanges && !bHasChanges) return -1;
      if (!aHasChanges && bHasChanges) return 1;
      return 0;
    });

    return sorted;
  }, [data, beneficialOwnershipChanges]);

  // Determine if editing should be enabled based on manual update mode
  const shouldHideEditBtn = hideEditBtn;
  const shouldHideDeleteBtn = hideDeleteBtn;

  let onClick = (row) => {
    if (!row) return;
    const canToggleAll = typeof row.toggleAllRowsExpanded === "function";
    const canToggleRow = typeof row.toggleRowExpanded === "function";

    if (!row.isExpanded) {
      if (canToggleAll) {
        row.toggleAllRowsExpanded(false); // Close all rows
      }
      if (canToggleRow) {
        row.toggleRowExpanded(); // Open the clicked row
      }
    } else {
      if (canToggleRow) {
        row.toggleRowExpanded(); // Toggle the clicked row
      }
    }
  };

  const handleClose = (row) => {
    setChangeOfParticularsIsSelected(false);
    onClick(row);
  };

  const handleChange = (row) => {
    setChangeOfParticularsInfo({
      selectedValueType: "changes-including-email",
    });
    setChangeOfParticularsIsSelected(true);
    onClick(row);
  };

  const getBoFullName = ({ names, remainingMinorityShareholders }) => {
    if (remainingMinorityShareholders && remainingMinorityShareholders >= 0)
      return (
        <span>{`${remainingMinorityShareholders} Minority Shareholders`}</span>
      );
    return getFullName(names);
  };

  const getLinkedName = (row) => {
    const { linkToCompany, shareholderWithBO, JSCombinedName } = row;

    if (JSCombinedName) return JSCombinedName;
    if (linkToCompany) {
      const linkToCompanyBO = allBeneficialOwners.find(
        (bo) => bo._id === linkToCompany || bo._id === linkToCompany?._id
      );
      if (!linkToCompanyBO) {
        if (
          !deleteBeneficialOwnerStatus.inProgressIds.includes(row._id) &&
          !disableDeleteEffect
        ) {
          dispatch(
            deleteBeneficialOwnerAsync({
              beneficialOwnerId: row._id,
              companyId,
            })
          );
        }
        return "";
      }
      if (linkToCompanyBO.linkToCompany || linkToCompanyBO.shareholderWithBO)
        return getLinkedName(linkToCompanyBO)
          ? `${getLinkedName(linkToCompanyBO)} -> ${getBoFullName(
              linkToCompanyBO
            )}`
          : getBoFullName(linkToCompanyBO);
      return getBoFullName(linkToCompanyBO);
    }

    if (shareholderWithBO) {
      const linkedCompany = allBeneficialOwners.find(
        (bo) => bo._id === shareholderWithBO
      );
      if (!linkedCompany) {
        if (
          !deleteBeneficialOwnerStatus.inProgressIds.includes(row._id) &&
          !disableDeleteEffect
        ) {
          dispatch(
            deleteBeneficialOwnerAsync({
              beneficialOwnerId: row._id,
              companyId,
            })
          );
        }
        return "";
      }
      if (linkedCompany.linkToCompany || linkedCompany.shareholderWithBO)
        return getLinkedName(linkedCompany)
          ? `${getLinkedName(linkedCompany)} -> ${getBoFullName(linkedCompany)}`
          : getBoFullName(linkedCompany);
      return getBoFullName(linkedCompany);
    }
    return "Directly";
  };

  const columns = [
    {
      Header: "Details",
      accessor: "fullName",
      Cell: ({ row: { original } }) => {
        if (!groupBOEntries) return <div className="w-56"> </div>;
        if (original?.remainingMinorityShareholders) {
          const minorityFullName = `${original.remainingMinorityShareholders} Minority Shareholders`;
          return <span>{minorityFullName}</span>;
        }
        const emailAddress =
          original?.emailAddress || original?.electronicAddress?.emailAddress;
        const fullName = getBoFullName(original);
        if (
          CORPORATES.includes(original.idType) ||
          original.idType === "Joint Shareholder"
        ) {
          return <span>{fullName}</span>;
        }

        const hasChanges =
          beneficialOwnershipChanges.has(original._id) || original.hasChanges;

        return (
          <div className="flex items-center space-x-1 w-52">
            <div className={`flex-shrink-0`}>
              {(() => {
                try {
                  const hasPhoto =
                    original &&
                    original.fileUploads &&
                    typeof original.fileUploads === "object" &&
                    original.fileUploads.passportPhoto &&
                    typeof original.fileUploads.passportPhoto === "string" &&
                    original.fileUploads.passportPhoto.length > 0;

                  if (hasPhoto) {
                    return (
                      <img
                        className="w-8 h-8 rounded-full"
                        src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${original.fileUploads.passportPhoto}`}
                        alt={"Bo Official"}
                      />
                    );
                  }
                } catch (error) {
                  console.warn("Error accessing passport photo:", error);
                }
                return (
                  <div className="w-8 h-8 rounded-full">
                    <div className="relative w-8 h-8 overflow-hidden rounded-full bg-transparent">
                      <AvatarIcon />
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="p-3">
              <h1 className="font-bold">{fullName}</h1>
              <p>
                {original?.type === "invited"
                  ? getMaskedEmail(emailAddress)
                  : emailAddress}
              </p>
              <p className="mt-1">{original.idType}</p>
            </div>
          </div>
        );
      },
    },
    {
      Header: "Link to Company",
      className: "p-4 text-ellipsis overflow-hidden max-w-[150px]",
      Cell: ({ row }) => {
        if (row.original?.LinkToCompanyLabel)
          return <div className="w-28">{row.original.LinkToCompanyLabel}</div>;
        if (row.original?.children) {
          const linkedCompanyNames = row.original.children?.map((child) =>
            getLinkedName(child)
          );
          if (linkedCompanyNames.length === 1)
            return <div className="w-28">{linkedCompanyNames}</div>;
          const lastName = linkedCompanyNames.pop();
          const linkedNames =
            linkedCompanyNames.join(", ") + " and " + lastName;
          return <div className="w-28">{linkedNames}</div>;
        }
        return <div className="w-28">{getLinkedName(row.original)}</div>;
      },
    },
    {
      Header: "Type Of Control",
      Cell: ({ row }) => {
        const officialChanges = beneficialOwnershipChanges.get(
          row.original._id
        );

        if (row.original?.typeOfControl) {
          return (
            <TypeOfControlColumn
              fields={row.original.typeOfControl.slice(0, 4)}
            />
          );
        }

        // Use the beneficialOwnershipForm from row.original (already updated by BeneficialOwnership)
        const beneficialOwnershipForm = row.original.beneficialOwnershipForm;
        const fields = getBeneficialOwnershipFormFields({
          beneficialOwnershipForm,
          reviewTab: true,
          renderShortTypeOfControl,
          beneficialOwner: row.original,
          beneficialOwners,
        });

        return <TypeOfControlColumn fields={fields.slice(0, 4)} />;
      },
    },
    {
      Header: "Cumulative Ownership",
      className: "w-32",
      Cell: ({ row: { original } }) => {
        const officialChanges = beneficialOwnershipChanges.get(original._id);

        let shareholdings, votings;

        if (
          _has(original, "cumulativeShareholdings") &&
          _has(original, "cumulativeVotings")
        ) {
          shareholdings = original.cumulativeShareholdings;
          votings = original.cumulativeVotings;
        } else {
          const calculated = calculateCumulativeOwnership(
            original,
            beneficialOwners
          );
          shareholdings = calculated.cumulativeShareholdings;
          votings = calculated.cumulativeVotings;
        }

        // Use updated values from beneficialOwnershipForm (already updated by BeneficialOwnership)

        const shareholdingChange =
          officialChanges?.changedFields?.maxPercentShareholding;
        const votingChange =
          officialChanges?.changedFields?.maxPercentVotingRights;

        const shareholdingDisplay = shareholdingChange ? (
          <Tooltip
            content={
              <div>
                <div style={{ color: "white" }}>
                  Previous:{" "}
                  {officialChanges?.currentDetails?.maxPercentShareholding !==
                  undefined
                    ? `${officialChanges.currentDetails.maxPercentShareholding}%`
                    : `${shareholdingChange.from || "N/A"}%`}
                </div>
              </div>
            }
            position="top"
          >
            <span className="border-b border-dashed border-green-500 bg-green-400 px-1 rounded cursor-help">
              {roundNumber(shareholdings)}%
            </span>
          </Tooltip>
        ) : (
          `${roundNumber(shareholdings)}%`
        );

        const votingDisplay = votingChange ? (
          <Tooltip
            content={
              <div>
                <div style={{ color: "white" }}>
                  Previous:{" "}
                  {officialChanges?.currentDetails?.maxPercentVotingRights !==
                  undefined
                    ? `${officialChanges.currentDetails.maxPercentVotingRights}%`
                    : `${votingChange.from || "N/A"}%`}
                </div>
              </div>
            }
            position="top"
          >
            <span className="border-b border-dashed border-green-500 bg-green-400 px-1 rounded cursor-help">
              {roundNumber(votings)}%
            </span>
          </Tooltip>
        ) : (
          `${roundNumber(votings)}%`
        );

        return (
          <div>
            {shareholdingDisplay}, {votingDisplay}
          </div>
        );
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        const isOwnerRow =
          currentUser.electronicAddress.emailAddress ===
          row.original?.electronicAddress?.emailAddress;

        let reviewBtnProps = {
          onClick: () => onClick(row),
        };
        const officialComments =
          comments.filter(
            (comment) =>
              comment?.officialId &&
              comment.officialId === row.original._id &&
              comment?.tabName &&
              comment.tabName === selectedTableType
          )?.length ?? 0;

        if (
          row.isExpanded &&
          changesBeneficialOwners?.includes(
            Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER
          )
        ) {
          return (
            <TableActionMenu
              row={row}
              showCloseBtn={true}
              hideEditBtn={true}
              hideDeleteBtn={true}
              handleClose={handleClose}
            />
          );
        }

        return (
          <div className="flex flex-col items-center relative">
            {showActionColumn &&
              (groupBOEntries
                ? !row.isExpanded || row.original?.children.length < 2
                : true) && (
                <TableActionMenu
                  row={row}
                  showViewBtn={role !== Constants.COOKIE_ROLES.OFFICIAL}
                  showReviewBtn={
                    !isOwnerRow &&
                    row.original?.type === "invited" &&
                    row.original.createdBy === currentUser._id &&
                    !row.original?.isReadOnly &&
                    !hideReviewWorkflow
                  }
                  renderViewAsSubRow
                  hideEditBtn={shouldHideEditBtn}
                  hideDeleteBtn={shouldHideDeleteBtn}
                  handleView={(row) => {
                    handleReview(row, { isViewOnly: true });
                  }}
                  handleReview={handleReview}
                  showChange={
                    changesBeneficialOwners &&
                    changesBeneficialOwners?.includes(
                      Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER
                    )
                  }
                  handleChange={handleChange}
                />
              )}
            {groupBOEntries &&
              !row.isExpanded &&
              row.original?.children &&
              row.original?.children?.length > 1 && (
                <button
                  type="button"
                  {...reviewBtnProps}
                  ref={collapseBtnRef}
                  className={`mt-32 text-gray-900 bg-gray-200 rounded-full items-center p-2`}
                >
                  <PlusIcon className="w-6 h-6" aria-hidden="true" />
                </button>
              )}
            {officialComments ? (
              <span className="absolute flex items-center justify-center min-w-[4] h-4 p-1 text-white bg-red-700 rounded-full -top-3 right-0">
                {officialComments}
              </span>
            ) : null}
          </div>
        );
      },
    },
  ].filter(Boolean);

  const handleReview = async (row, options = {}) => {
    if (!row.linkToCompany) {
      handleBackBtn();
      await delay(); // wait for the company officials tab to be mounted
      let companyShareholderReviewBtn = null;
      if (row?.JSGroupID)
        companyShareholderReviewBtn = document.querySelector(
          `[id="${row?.JSGroupID}_reviewButton"]`
        );
      else if (row?._id && !row.JSGroupID)
        companyShareholderReviewBtn = document.querySelector(
          `[id="${row?._id}_reviewButton"]`
        );
      if (companyShareholderReviewBtn) companyShareholderReviewBtn.click();
      return;
    }
    const viewOnly = options?.isViewOnly ?? row?.isViewOnly ?? false;
    if (!viewOnly && !row?.isReadOnly) setIsViewOnly(false);
    else setIsViewOnly(true);
    onClick(row);
  };

  const renderRowSubComponent = React.useCallback(
    ({ row, rowProps, visibleColumns }) => {
      if (changeOfParticularsIsSelected) {
        const stakeholderOfficial = originalCompanyOfficials.find(
          (official) =>
            official?.names?.firstName === row.original?.names?.firstName &&
            official?.names?.lastName === row.original?.names?.lastName
        );
        return (
          <ChangeOfParticularsSubRow
            row={row}
            visibleColumns={visibleColumns}
            changeList={[]}
            setChangeList={() => null}
            actionType=""
            stakeholderOfficial={stakeholderOfficial}
            isBeneficialOwner={true}
          />
        );
      }
      return (
        <BeneficialOwnersSubRowAsync
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          currentTab={selectedTableType}
          handleBackBtn={handleBackBtn}
          selectedTableType={selectedTableType}
          hideEditBtn={shouldHideEditBtn}
          hideDeleteBtn={shouldHideDeleteBtn}
          isViewOnly={isViewOnly}
          hideReviewWorkflow={hideReviewWorkflow}
          isViewMode={restProps.isViewMode}
          hideResolveAllCheckbox
          beneficialOwners={beneficialOwners}
          showDocumentLabel={showDocumentLabel}
          isInManualUpdate={isInManualUpdate}
          beneficialOwnershipChanges={beneficialOwnershipChanges}
          transactions={transactions}
        />
      );
    },
    [
      changeOfParticularsIsSelected,
      shouldHideEditBtn,
      shouldHideDeleteBtn,
      isInManualUpdate,
    ]
  );

  return (
    <>
      <NotificationBanner notificationKey="flashMessageBOTable" />
      <div className="w-full bg-white shadow">
        <div className="w-full overflow-x-auto">
          <ReactTable
            columns={columns}
            data={sortedData}
            dataRowStyle={(row) => {
              const hasChanges =
                beneficialOwnershipChanges.has(row?.original?._id) ||
                row?.original?.hasChanges;
              const hasChangedFields =
                row?.original?.changedFields &&
                Object.keys(row?.original?.changedFields).length > 0;

              const baseStyle = `h-12 text-sm ${
                !groupBOEntries
                  ? "border-white border-b-0"
                  : "border-b border-t border-gray-200"
              }`;

              const style =
                (isInManualUpdate && hasChanges) ||
                (isInUpdateCompanyDetailsContext && hasChangedFields)
                  ? `${baseStyle} text-gray-800 bg-gray-300`
                  : `${baseStyle} bg-white text-gray-700 hover:bg-gray-50`;

              if (isInManualUpdate && hasChanges) {
              }

              return style;
            }}
            dataRowCellStyle={(row) => {
              const hasChanges =
                beneficialOwnershipChanges.has(row?.original?._id) ||
                row?.original?.hasChanges;
              return isInManualUpdate && hasChanges
                ? "whitespace-normal px-4 text-gray-800 bg-inherit"
                : "whitespace-normal px-4 text-gray-700 bg-inherit";
            }}
            renderRowSubComponent={renderRowSubComponent}
            hideColumnsRow={hideColumnsRow}
          />
        </div>
      </div>
    </>
  );
};

export default BeneficialOwnersTableReview;
